import { Outside } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openBookingCalendar, setDates, setError, toggleCalendarModal } from 'shared/actions/dailyrentBookingCalendar';
import { BookingCalendar } from 'shared/components/BookingCalendar';
import { BookingCalendarLayout } from 'shared/components/BookingCalendarLayout';
import { BookingCalendarTooltip } from 'shared/components/BookingCalendarTooltip';
import { deleteDailyrentQueryParams, formatDate } from 'shared/containers/BookingCalendarInputContainer/helpers';
import {
  useCalendarPageDiff,
  usePreparedBookingCalendarData,
} from 'shared/containers/BookingCalendarInputContainer/hooks';
import {
  selectDailyrentBookingCalendarCanBeBooked,
  selectDailyrentBookingCalendarDateInputText,
  selectDailyrentBookingCalendarError,
  selectDailyrentBookingCalendarOpen,
} from 'shared/selectors/dailyrentBookingCalendar';
import { selectOfferId } from 'shared/selectors/offerData/offer/selectOfferId';
import {
  trackCalendarInputClick,
  trackCalendarInputShow,
  trackMinimumDurationHint,
} from 'shared/tracking/dailyrentBookingCalendar';

import { BookingCalendarInput } from '../../components/BookingCalendarInput';

export const DEFAULT_INPUT_TEXT = 'Заезд – Выезд';

export const BookingCalendarInputContainer = () => {
  const dispatch = useDispatch();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tooltipAnchorRef = React.useRef<HTMLElement | null>(null);

  const calendarOpen = useSelector(selectDailyrentBookingCalendarOpen);
  const canBeBooked = useSelector(selectDailyrentBookingCalendarCanBeBooked);
  const dateInputText = useSelector(selectDailyrentBookingCalendarDateInputText);
  const error = useSelector(selectDailyrentBookingCalendarError);
  const offerId = useSelector(selectOfferId);

  const preparedData = usePreparedBookingCalendarData();
  const [showTooltip, setShowTooltip] = React.useState(false);

  const { calendarPageDiff, handleNextClick, handlePrevClick } = useCalendarPageDiff(error);

  const title = dateInputText || DEFAULT_INPUT_TEXT;
  const isDefaultText = title === DEFAULT_INPUT_TEXT;

  const handleClick = React.useCallback(() => {
    if (!calendarOpen) {
      dispatch(openBookingCalendar());
      trackCalendarInputClick(offerId);

      return;
    }

    dispatch(setError(''));
    dispatch(toggleCalendarModal(false));

    return;
  }, [calendarOpen, offerId]);

  const handleOutsideClick = React.useCallback(() => {
    dispatch(setError(''));
    dispatch(toggleCalendarModal(false));
  }, []);

  // если при загрузке карточки с бэка пришло, что заданные даты недоступны,
  // убираем их из урлы и зануляем в сторе
  React.useEffect(() => {
    if (!canBeBooked) {
      const newUrl = deleteDailyrentQueryParams(window.location.href);
      history.replaceState(undefined, '', newUrl);
      dispatch(setDates({ from: undefined, to: undefined }));
    }
  }, []);

  React.useEffect(() => {
    trackCalendarInputShow(offerId);
  }, [offerId]);

  React.useEffect(() => {
    // находимся ли на той же странице календаря, на которой появился тултип
    const onTheSameCalendarPage = !!calendarPageDiff;
    if (!containerRef.current || !preparedData.toValue || !error || onTheSameCalendarPage) {
      setShowTooltip(false);
      tooltipAnchorRef.current = null;

      return;
    }

    tooltipAnchorRef.current = containerRef.current.querySelector(
      `[data-date="${formatDate(preparedData.toValue)}"]`,
    ) as HTMLElement;

    trackMinimumDurationHint(offerId);
    setShowTooltip(true);
  }, [preparedData.toValue, error, calendarPageDiff]);

  return (
    <Outside onOutside={handleOutsideClick} active={calendarOpen} insideRefs={undefined}>
      <BookingCalendarLayout ref={containerRef}>
        <BookingCalendarInput title={title} onClick={handleClick} isDefaultText={isDefaultText} />
        <BookingCalendar
          calendarOpen={calendarOpen}
          preparedData={{
            ...preparedData,
            onNextClick: handleNextClick,
            onPrevClick: handlePrevClick,
          }}
        />
        <BookingCalendarTooltip anchorRef={tooltipAnchorRef} error={error} open={showTooltip && calendarOpen} />
      </BookingCalendarLayout>
    </Outside>
  );
};
